import { useState, useEffect } from 'react'
import { apiFetch } from '../utils/api'
import SoftwareProductForm from './SoftwareProductForm'
import ProductPlanAdministrationContent from './ProductPlanAdministrationContent'
import { Eye, Lock } from 'lucide-react'

const SoftwareProductAdministrationContent = () => {
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editingProduct, setEditingProduct] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [deleteTarget, setDeleteTarget] = useState(null)

  // Load software products from API
  const loadProducts = async () => {
    try {
      const response = await apiFetch('/api/admin/software-products')
      if (response.ok) {
        const data = await response.json()
        setProducts(data.software_products || [])
      } else {
        setError('Failed to load software products')
      }
    } catch (err) {
      setError('Network error while loading software products')
      console.error('Error loading software products:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadProducts()
  }, [])

  const handleAdd = () => {
    setEditingProduct(null)
    setIsEditing(false)
    setShowForm(true)
  }

  const handleEdit = (product) => {
    setEditingProduct(product)
    setIsEditing(true)
    setShowForm(true)
  }

  const handleDelete = (product) => {
    setDeleteTarget(product)
  }

  const confirmDelete = async (product) => {
    try {
      const response = await apiFetch(`/api/admin/software-products/${product.id}`, { method: 'DELETE' })
      if (response.ok) {
        setProducts(prev => prev.filter(p => p.id !== product.id))
      } else {
        const data = await response.json()
        setError(data.error || 'Failed to delete software product')
      }
    } catch (err) {
      setError('Network error. Please check backend server.')
      console.error('Delete software product error:', err)
    } finally {
      setDeleteTarget(null)
    }
  }

  const handleSave = (savedProduct) => {
    if (isEditing) {
      setProducts(prev => prev.map(p => p.id === savedProduct.id ? savedProduct : p))
    } else {
      setProducts(prev => [...prev, savedProduct])
    }
    setShowForm(false)
    setEditingProduct(null)
    setIsEditing(false)
  }

  const handleCancel = () => {
    setShowForm(false)
    setEditingProduct(null)
    setIsEditing(false)
  }

  if (loading) {
    return (
      <div className="content-section">
        <h2>Software Products</h2>
        <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
          <div className="loading-spinner" style={{ margin: '0 auto 16px' }}></div>
          Loading...
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="content-section">
        <h2>Software Products</h2>
        <div className="error-message">{error}</div>
        <button className="btn-primary" onClick={loadProducts}>Retry</button>
      </div>
    )
  }

  // If a product is selected for plan management, render the plan UI
  if (selectedProduct) {
    return (
      <ProductPlanAdministrationContent
        softwareProduct={selectedProduct}
        onBack={() => setSelectedProduct(null)}
      />
    )
  }

  return (
    <div className="content-section">
      <h2>Software Products</h2>
      <div className="user-controls">
        <button className="btn-primary" onClick={handleAdd}>Add New Product</button>
      </div>
      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th style={{ width: 'fit-content', textAlign: 'center' }}>Status</th>
              <th>Name</th>
              <th>Slug</th>
              <th>Version</th>
              <th>AppStore Only</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.length === 0 ? (
              <tr><td colSpan="7" style={{ textAlign: 'center', color: '#9ca3af' }}>No products found</td></tr>
            ) : (
              products.map(prod => (
                <tr key={prod.id}>
                  <td>{prod.id}</td>
                  <td style={{ textAlign: 'center' }} title={prod.visible ? 'Sichtbar' : 'Versteckt'}>
                    {prod.visible ? <Eye size={18} color="#86efac" /> : <Lock size={18} color="#fca5a5" />}
                  </td>
                  <td>{prod.name}</td>
                  <td>{prod.slug}</td>
                  <td>{prod.version || '1.0.0'}</td>
                  <td>{prod.distribution_type === 'appstore' ? '✓' : '✗'}</td>
                  <td>
                    <button className="btn-edit" onClick={() => handleEdit(prod)}>Edit</button>
                    <button className="btn-delete" onClick={() => handleDelete(prod)}>Delete</button>
                    {prod.distribution_type !== 'appstore' && (
                      <button
                        className="btn-primary"
                        onClick={() => setSelectedProduct(prod)}
                        title="Manage Plans"
                      >
                        Manage Plans
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <SoftwareProductForm
          product={editingProduct}
          onSave={handleSave}
          onCancel={handleCancel}
          isEditing={isEditing}
        />
      )}
      {deleteTarget && (
        <div className="user-form-overlay">
          <div className="user-form-container">
            <div className="user-form-header">
              <h3>Delete Product</h3>
              <button className="close-btn" onClick={() => setDeleteTarget(null)}>×</button>
            </div>
            <p style={{ color: '#e5e7eb', marginBottom: '16px' }}>
              Are you sure you want to delete the product "{deleteTarget.name}"? This action cannot be undone.
            </p>
            <div className="form-actions">
              <button type="button" className="btn-cancel" onClick={() => setDeleteTarget(null)}>Cancel</button>
              <button type="button" className="btn-delete" onClick={() => confirmDelete(deleteTarget)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SoftwareProductAdministrationContent 